// Registry of creator-app pages the Deep Link Generator (pages/LinkGenerator.tsx)
// can build a shareable CTA link for. Every link resolves to
// `${BRAND_DASHBOARD_URL}/l/:key/:id`, a web→app redirect page in
// Brand-Dashboard (src/pages/AppLinkRedirect.tsx) that opens the matching
// bmc://... deep link with an App/Play Store fallback and QR code.
//
// Keep `key` in sync with Brand-Dashboard's copy of this list
// (Brand-Dashboard/src/lib/deepLinkPages.ts) — the two repos don't share a
// package, so a page added on one side needs the same `key` added on the
// other for the link to actually resolve.
export interface DeepLinkPage {
  key: string
  label: string
  group: 'Main pages' | 'Detail pages (need an ID)'
  requiresId: boolean
  idLabel?: string
  idPlaceholder?: string
  appPath: (id?: string) => string
}

export const DEEP_LINK_PAGES: DeepLinkPage[] = [
  { key: 'feed', label: 'Feed (Home)', group: 'Main pages', requiresId: false, appPath: () => '' },
  { key: 'campaigns', label: 'Campaigns', group: 'Main pages', requiresId: false, appPath: () => 'campaigns' },
  { key: 'jobs', label: 'Jobs — Browse', group: 'Main pages', requiresId: false, appPath: () => 'jobs' },
  { key: 'discover', label: 'Hidden Gems (Products)', group: 'Main pages', requiresId: false, appPath: () => 'discover' },
  { key: 'leaderboard', label: 'Leaderboard / Join CAP', group: 'Main pages', requiresId: false, appPath: () => 'leaderboard' },
  // { key: 'referral', label: 'Referral Program', group: 'Main pages', requiresId: false },
  { key: 'my-products', label: 'My Products', group: 'Main pages', requiresId: false, appPath: () => 'my-products' },
  { key: 'upgrade', label: 'Upgrade to Pro', group: 'Main pages', requiresId: false, appPath: () => 'upgrade' },
  {
    key: 'campaign-detail',
    label: 'Campaign Detail',
    group: 'Detail pages (need an ID)',
    requiresId: true,
    idLabel: 'Campaign ID',
    idPlaceholder: 'e.g. 8f3a2c10-4b1e-4a9d-9c2f-1e2d3c4b5a6f',
    appPath: (id) => `campaign/${id}`,
  },
  {
    key: 'job-detail',
    label: 'Job Detail',
    group: 'Detail pages (need an ID)',
    requiresId: true,
    idLabel: 'Job ID',
    idPlaceholder: 'e.g. 8f3a2c10-4b1e-4a9d-9c2f-1e2d3c4b5a6f',
    appPath: (id) => `job/${id}`,
  },
  {
    key: 'offer-detail',
    label: 'Offer Detail',
    group: 'Detail pages (need an ID)',
    requiresId: true,
    idLabel: 'Hire / Offer ID',
    idPlaceholder: 'e.g. 8f3a2c10-4b1e-4a9d-9c2f-1e2d3c4b5a6f',
    appPath: (id) => `offer/${id}`,
  },
]

export function findDeepLinkPage(key: string): DeepLinkPage | undefined {
  return DEEP_LINK_PAGES.find((p) => p.key === key)
}

// Falls back to production so the tool works out of the box even if the env
// var isn't set locally — matches the pattern Brand-Dashboard's worker uses
// for PUBLIC_DASHBOARD_URL (worker/src/hiring.ts).
export const BRAND_DASHBOARD_URL =
  (import.meta.env.VITE_BRAND_DASHBOARD_URL || 'https://app.brandsmeetcreators.com').replace(/\/+$/, '')

export function buildDeepLinkUrl(key: string, id?: string): string {
  const path = id ? `/l/${encodeURIComponent(key)}/${encodeURIComponent(id)}` : `/l/${encodeURIComponent(key)}`
  return `${BRAND_DASHBOARD_URL}${path}`
}

// Native bmc:// deep link, used as the `deep_link` field when an admin
// broadcasts a push notification so tapping it routes inside the creator app.
// E.g. key='campaign-detail', id='abc' → 'bmc://campaign/abc'
export function buildBmcDeepLink(key: string, id?: string): string {
  const page = findDeepLinkPage(key)
  const path = page ? page.appPath(id) : ''
  return `bmc://${path}`
}
